"use client"

import { useState, useEffect } from "react"
import { useTheme } from "../../../contexts/ThemeContext"
import { useAuth } from "../../../contexts/AuthContext"
import { useNavigate, Link } from "react-router-dom"
import { API_BASE_URL } from "../../../config/api"
import { EyeIcon, EyeSlashIcon, SunIcon, MoonIcon, ArrowLeftIcon, ArrowPathIcon, ShieldCheckIcon, BoltIcon, PhoneIcon } from "@heroicons/react/24/outline"

const SignIn = ({ onSignIn }) => {
  const { isDark, toggleTheme } = useTheme()
  const { login, verifyTwoFactorLogin, forgotPassword, verifyResetCode, resetPassword, effectiveLogo } = useAuth()
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    keepLoggedIn: true,
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const [agreeTerms, setAgreeTerms] = useState(true)
  const [agreePrivacy, setAgreePrivacy] = useState(true)

  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1) // 1: email, 2: code, 3: new password
  const [resetToken, setResetToken] = useState("")
  const [resetCode, setResetCode] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [twoFactorToken, setTwoFactorToken] = useState("")
  const [twoFactorCode, setTwoFactorCode] = useState("")

  const [captchaData, setCaptchaData] = useState({ id: "", svg: "" })
  const [captchaValue, setCaptchaValue] = useState("")

  const fetchCaptcha = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/captcha?t=${Date.now()}`)
      const data = await res.json()
      if (data.captchaId) {
        setCaptchaData({ id: data.captchaId, svg: data.svg })
        setCaptchaValue("")
      }
    } catch (err) {
      console.error("Failed to fetch captcha", err) 
    }
  }

  // Fetch captcha on mount
  useEffect(() => {
    fetchCaptcha()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      const result = await login(formData.email, formData.password, captchaData.id, captchaValue)

      if (result?.success) {
        if (result.requiresTwoFactor) {
          setTwoFactorToken(result.twoFactorToken)
          setSuccess(result.message || "Enter the two-factor code from your authenticator app.")
          return
        }

        setSuccess(`Login successful!`)
        setTimeout(() => {
          if (result.user?.role === "support") {
            navigate("/queue", { replace: true })
          } else {
            navigate("/user-accounts", { replace: true })
          }
        }, 1200)
      } else {
        setError(result?.error || "Invalid email, password, or CAPTCHA")
        fetchCaptcha() // refresh on failure
      }
    } catch (err) {
      console.error("Login handleSubmit error:", err)
      setError(err?.message || "An unexpected error occurred")
      fetchCaptcha()
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyTwoFactor = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      const result = await verifyTwoFactorLogin(twoFactorToken, twoFactorCode)

      if (result.success) {
        setSuccess("Login successful!")
        setTimeout(() => {
          if (result.user?.role === "support") {
            navigate("/queue", { replace: true })
          } else {
            navigate("/user-accounts", { replace: true })
          }
        }, 1200)
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError("Failed to verify two-factor code")
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      const result = await forgotPassword(formData.email)

      if (result.success) {
        setResetToken(result.resetToken)
        setForgotPasswordStep(2)
        setSuccess("Verification code sent to your email!")
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError("Failed to send reset code")
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyResetCode = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      const result = await verifyResetCode(resetToken, resetCode)

      if (result.success) {
        setForgotPasswordStep(3)
        setSuccess("Code verified! Enter your new password.")
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError("Invalid verification code")
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      const result = await resetPassword(resetToken, resetCode, newPassword)

      if (result.success) {
        setSuccess("Password reset successfully! You can now sign in.")
        setShowForgotPassword(false)
        setForgotPasswordStep(1)
        setResetToken("")
        setResetCode("")
        setNewPassword("")
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError("Failed to reset password")
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const resetForgotPasswordFlow = () => {
    setShowForgotPassword(false)
    setForgotPasswordStep(1)
    setResetToken("")
    setResetCode("")
    setNewPassword("")
    setError("")
    setSuccess("")
  }

  const resetTwoFactorFlow = () => {
    setTwoFactorToken("")
    setTwoFactorCode("")
    setError("")
    setSuccess("")
  }

  return (
    <div
      className="h-screen w-full flex items-start sm:items-center justify-center px-4 relative overflow-x-hidden overflow-y-auto"
      style={{
        backgroundImage: isDark
          ? `url('https://www.absolutegeeks.com/wp-content/uploads/2024/10/meta.gif')`
          : `url('https://images.squarespace-cdn.com/content/v1/66688035ea92183bf5447f7d/1718124616198-0HSKJJC0H0YESCSQ0A5W/meta_P3_logo_shapeExplore5_3D_v01.gif')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="absolute inset-0 bg-blue-50/70 dark:bg-gray-900/80 backdrop-blur-sm"></div>

      {/* Main Container */}
      <div className="relative z-10 w-full flex">

        {/* Left Column (Branding & Features) */}
        <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 text-gray-900 dark:text-white relative">
          {/* Logo area */}
          <div className="flex items-center z-10 min-h-[48px]">
            {effectiveLogo ? (
              <img
                src={effectiveLogo}
                alt="Logo"
                className="h-12 w-auto max-w-[220px] object-contain"
              />
            ) : null}
          </div>

          {/* Main Content */}
          <div className="max-w-lg mt-8 z-10">
            <div className="inline-block px-4 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-sm font-semibold mb-6">
              Welcome Back!
            </div>
            <h1 className="text-5xl font-bold mb-2 tracking-tight">Welcome back!</h1>
            <h2 className="text-5xl font-bold text-blue-600 dark:text-blue-400 mb-6 tracking-tight">Glad to see you again</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-12 text-lg">
              Access your account and continue where you left off. Secure, simple and seamless.
            </p>

            <div className="space-y-8">
              {/* Feature 1 */}
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-50 dark:bg-gray-800/80 rounded-xl text-blue-600 dark:text-blue-400 shadow-sm border border-white/40 dark:border-gray-700/50">
                  <ShieldCheckIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Secure Access</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Your data is protected with top-level security</p>
                </div>
              </div>
              {/* Feature 2 */}
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-50 dark:bg-gray-800/80 rounded-xl text-blue-600 dark:text-blue-400 shadow-sm border border-white/40 dark:border-gray-700/50">
                  <BoltIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Fast & Simple</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Quick login experience, minimal steps</p>
                </div>
              </div>
              {/* Feature 3 */}
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-50 dark:bg-gray-800/80 rounded-xl text-blue-600 dark:text-blue-400 shadow-sm border border-white/40 dark:border-gray-700/50">
                  <PhoneIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">24/7 Support</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">We're here to help you anytime</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400 mt-12 z-10 font-medium">
            © 2026 Digiforest. All rights reserved.
          </div>
        </div>

        {/* Right Column (Sign In Form) */}
        <div className="w-full lg:w-1/2 flex items-center justify-center relative min-h-full">
          {/* Theme toggle button in the top right corner */}
          <div className="absolute top-6 right-6 lg:top-8 lg:right-8 z-20">
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors rounded-full hover:bg-black/5 dark:hover:bg-white/10"
              title="Toggle theme"
            >
              {isDark ? <SunIcon className="w-6 h-6" /> : <MoonIcon className="w-6 h-6" />}
            </button>
          </div>

          {/* Form Container */}
          <div className="w-full max-w-lg z-10 relative mt-8 mb-8 sm:mt-0">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 sm:p-8 border border-gray-100 dark:border-gray-700">
              {/* Reseller Logo */}
              {effectiveLogo ? (
                <div className="flex justify-center mb-6">
                  <img
                    src={effectiveLogo}
                    alt="Logo"
                    className="h-12 w-auto max-w-[200px] object-contain"
                  />
                </div>
              ) : null}


              {error && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg text-green-700 dark:text-green-300 text-sm">
                  {success}
                </div>
              )}

              {!showForgotPassword ? (
                twoFactorToken ? (
                  <>
                    <div className="mb-4">
                      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 text-balance">
                        Two-Factor Verification
                      </h1>
                      <p className="text-gray-600 dark:text-gray-400 text-pretty">
                        Enter the 6-digit code from your authenticator app.
                      </p>
                    </div>

                    <form onSubmit={handleVerifyTwoFactor} className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          Verification Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={twoFactorCode}
                          onChange={(e) => setTwoFactorCode(e.target.value)}
                          className="w-full px-3 py-3 bg-gray-50/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm text-center text-2xl tracking-widest"
                          placeholder="000000"
                          maxLength="6"
                          required
                          disabled={loading}
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:shadow-none"
                      >
                        {loading ? "Verifying..." : "Verify and sign in"}
                      </button>

                      <button
                        type="button"
                        onClick={resetTwoFactorFlow}
                        className="w-full text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium py-2 transition-colors"
                        disabled={loading}
                      >
                        Back to Sign In
                      </button>
                    </form>
                  </>
                ) : (
                  <>
                    {/* Form header */}
                    <div className="mb-4">
                      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 text-balance">Sign In</h1>
                      <p className="text-gray-600 dark:text-gray-400 text-pretty">
                        Enter your email and password to sign in!
                      </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Email field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-3 py-3 bg-gray-50/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm"
                          placeholder="info@gmail.com"
                          required
                          disabled={loading}
                        />
                      </div>

                      {/* Password field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className="w-full px-3 py-3 bg-gray-50/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-12 backdrop-blur-sm"
                            placeholder="Enter your password"
                            required
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            disabled={loading}
                          >
                            {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>

                      {/* CAPTCHA field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          CAPTCHA <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-col sm:flex-row sm:items-stretch space-y-3 sm:space-y-0 sm:space-x-3">
                          <div className="flex items-stretch space-x-2 w-full sm:w-7/12">
                            <div
                              className="flex-grow bg-white border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden flex items-center justify-center min-h-[60px] dark:invert dark:hue-rotate-180 transition-all duration-200 [&>svg]:w-full [&>svg]:h-full [&>svg]:object-cover [&_path:not([fill='none'])]:!fill-gray-900"
                              dangerouslySetInnerHTML={{ __html: captchaData.svg }}
                            />
                            <button
                              type="button"
                              onClick={fetchCaptcha}
                              className="px-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors text-gray-600 dark:text-gray-300 flex-shrink-0 flex items-center justify-center"
                              title="Refresh CAPTCHA"
                            >
                              <ArrowPathIcon className="w-6 h-6" />
                            </button>
                          </div>
                          {/* Input Field */}
                          <div className="relative w-full sm:w-5/12 flex-shrink-0">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                              <ShieldCheckIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </div>
                            <input
                              type="text"
                              value={captchaValue}
                              onChange={(e) => setCaptchaValue(e.target.value)}
                              className="w-full h-full pl-11 pr-4 py-3 bg-white dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm"
                              placeholder="Enter CAPTCHA"
                              required
                              disabled={loading}
                              maxLength={6}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Keep logged in and forgot password */}
                      <div className="flex items-center justify-between pt-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="keepLoggedIn"
                            checked={formData.keepLoggedIn}
                            onChange={handleChange}
                            className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2 transition-colors"
                            disabled={loading}
                          />
                          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">Keep me logged in</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors font-medium"
                          disabled={loading}
                        >
                          Forgot password?
                        </button>
                      </div>

                      <div className="flex flex-col space-y-3 pt-1 pb-1">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={agreeTerms}
                            onChange={(e) => setAgreeTerms(e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2 transition-colors"
                            required
                          />
                          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">
                            I Agree for <Link to="/terms" target="_blank" className="text-blue-600 dark:text-blue-400 hover:underline">Terms and Condition</Link>
                          </span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={agreePrivacy}
                            onChange={(e) => setAgreePrivacy(e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2 transition-colors"
                            required
                          />
                          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">
                            I Agree for <Link to="/privacy" target="_blank" className="text-blue-600 dark:text-blue-400 hover:underline">Privacy & Policy</Link>
                          </span>
                        </label>
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:shadow-none"
                      >
                        {loading ? "Signing in..." : "Sign in"}
                      </button>
                    </form>
                  </>
                )
              ) : (
                <>
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 text-balance">
                      {forgotPasswordStep === 1 && "Forgot Password"}
                      {forgotPasswordStep === 2 && "Verify Code"}
                      {forgotPasswordStep === 3 && "Reset Password"}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 text-pretty">
                      {forgotPasswordStep === 1 && "Enter your email to receive a verification code"}
                      {forgotPasswordStep === 2 && "Enter the 6-digit code sent to your email"}
                      {forgotPasswordStep === 3 && "Enter your new password"}
                    </p>
                  </div>

                  {forgotPasswordStep === 1 && (
                    <form onSubmit={handleForgotPassword} className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-4 bg-gray-50/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm"
                          placeholder="Enter your email"
                          required
                          disabled={loading}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:shadow-none"
                      >
                        {loading ? "Sending..." : "Send Reset Code"}
                      </button>
                      <button
                        type="button"
                        onClick={resetForgotPasswordFlow}
                        className="w-full text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium py-2 transition-colors"
                      >
                        Back to Sign In
                      </button>
                    </form>
                  )}

                  {forgotPasswordStep === 2 && (
                    <form onSubmit={handleVerifyResetCode} className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          Verification Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={resetCode}
                          onChange={(e) => setResetCode(e.target.value)}
                          className="w-full px-4 py-4 bg-gray-50/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm text-center text-2xl tracking-widest"
                          placeholder="000000"
                          maxLength="6"
                          required
                          disabled={loading}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:shadow-none"
                      >
                        {loading ? "Verifying..." : "Verify Code"}
                      </button>
                    </form>
                  )}

                  {forgotPasswordStep === 3 && (
                    <form onSubmit={handleResetPassword} className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          New Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full px-4 py-4 bg-gray-50/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-12 backdrop-blur-sm"
                            placeholder="Enter new password"
                            minLength="6"
                            required
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            disabled={loading}
                          >
                            {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:shadow-none"
                      >
                        {loading ? "Resetting..." : "Reset Password"}
                      </button>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignIn
